import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Collapse,
  IconButton,
  VStack,
  Badge,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Spinner,
  Flex,
  ButtonGroup,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  PracticeAttemptScoreStatusByPracticeSet,
} from "../../../Platform/Common/types";
import { fmtLocal } from "../../../utils/dates";

type QueueStatus = "PENDING" | "COMPLETED";

const PracticeManualAttemptStatus = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [queueStatus, setQueueStatus] = useState<QueueStatus>("PENDING");

  const [examAttemptScoringStatuses, setExamAttemptScoringStatuses] =
    useState<PracticeAttemptScoreStatusByPracticeSet[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [expandedAttempts, setExpandedAttempts] = useState<{ [key: number]: boolean }>({});

  const fetchPracticeAttemptScoringStatuses = async (status: QueueStatus) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}/b2c/practice-exam/manual-scoring/status`,
        {
          params: { status },
          headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
        }
      );
      setExamAttemptScoringStatuses(response.data);
    } catch (error) {
      console.error("Error fetching manual scoring statuses:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPracticeAttemptScoringStatuses(queueStatus);
  }, [queueStatus]);

  const toggleExpand = (attemptId: number) => {
    setExpandedAttempts((prev) => ({
      ...prev,
      [attemptId]: !prev[attemptId],
    }));
  };

  const handleOverride = (practiceAttemptId: number) => {
    navigate(`/practice/app/practice-manual-attempt/${practiceAttemptId}/manual-score`);
  };

  const handleViewAttempt = (practiceAttemptId: number) => {
    navigate(`/practice/app/practice-manual-attempt/${practiceAttemptId}/view`);
  };

  return (
    <Box mx="auto" overflowY="auto" p={4}>
      <Flex align="center" justify="space-between" mb={4}>
        <Heading size="md">
          Manual Scoring Queue
        </Heading>

        <ButtonGroup size="sm" isAttached variant="outline">
          <Button
            colorScheme={queueStatus === "PENDING" ? "purple" : "gray"}
            variant={queueStatus === "PENDING" ? "solid" : "outline"}
            onClick={() => setQueueStatus("PENDING")}
          >
            Pending
          </Button>
          <Button
            colorScheme={queueStatus === "COMPLETED" ? "purple" : "gray"}
            variant={queueStatus === "COMPLETED" ? "solid" : "outline"}
            onClick={() => setQueueStatus("COMPLETED")}
          >
            Completed (Last 6 Months)
          </Button>
        </ButtonGroup>
      </Flex>

      {loading ? (
        <Flex align="center" justify="center" mt={10}>
          <Spinner size="lg" />
        </Flex>
      ) : !examAttemptScoringStatuses || examAttemptScoringStatuses.length === 0 ? (
        <Box p={6}>
          <Text color="gray.500">
            {queueStatus === "PENDING"
              ? "No attempts pending manual scoring."
              : "No completed manual scoring records in the last 6 months."}
          </Text>
        </Box>
      ) : (
        <Accordion allowMultiple defaultIndex={[0]}>
          {examAttemptScoringStatuses.map((practiceSet) => (
            <AccordionItem
              key={practiceSet.practice_set_id}
              borderWidth="1px"
              borderRadius="md"
              mb={4}
            >
              <h2>
                <AccordionButton _expanded={{ bg: "blue.50" }}>
                  <Box as="span" flex="1" textAlign="left">
                    <Heading size="sm">
                      Practice Set: {practiceSet.practice_set_title}
                    </Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>

              <AccordionPanel pb={4}>
                <Accordion allowMultiple>
                  {practiceSet.practice_exam_attempts_score_status.map((exam) => (
                    <AccordionItem
                      key={exam.practice_set_exam_id}
                      borderWidth="1px"
                      borderRadius="md"
                      mb={3}
                      bg="gray.50"
                    >
                      <h3>
                        <AccordionButton _expanded={{ bg: "blue.100" }}>
                          <Box as="span" flex="1" textAlign="left">
                            <Heading size="xs">
                              Exam: {exam.practice_set_exam__title}
                            </Heading>
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h3>

                      <AccordionPanel pb={4}>
                        <Table variant="simple" size="sm">
                          <Thead bg="gray.100">
                            <Tr>
                              <Th>Student Name</Th>
                              <Th>Manual Scoring</Th>
                              <Th>Auto Scoring</Th>
                              <Th>Qset Status</Th>
                              <Th>View Attempt Score</Th>
                              <Th>{queueStatus === "PENDING" ? "Override Evaluation" : "Evaluation Record"}</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {exam.practice_attempts_score_status.map((attempt) => (
                              <React.Fragment key={attempt.practice_attempt_id}>
                                <Tr>
                                  <Td>{attempt.student_name}</Td>
                                  <Td>
                                    <Badge
                                      colorScheme={attempt.manual_scoring_completed ? "green" : "red"}
                                    >
                                      {attempt.manual_scoring_completed ? "Completed" : "Pending"}
                                    </Badge>
                                  </Td>
                                  <Td>
                                    <Badge
                                      colorScheme={attempt.auto_scoring_completed ? "green" : "red"}
                                    >
                                      {attempt.auto_scoring_completed ? "Completed" : "Pending"}
                                    </Badge>
                                  </Td>
                                  <Td>
                                    <IconButton
                                      aria-label="Toggle question set details"
                                      icon={
                                        expandedAttempts[attempt.practice_attempt_id] ? (
                                          <ChevronUpIcon />
                                        ) : (
                                          <ChevronDownIcon />
                                        )
                                      }
                                      onClick={() => toggleExpand(attempt.practice_attempt_id)}
                                      size="sm"
                                      variant="ghost"
                                    />
                                  </Td>
                                  <Td>
                                    <Button
                                      onClick={() => handleViewAttempt(attempt.practice_attempt_id)}
                                      size="xs"
                                      variant="ghost"
                                      colorScheme="blue"
                                      isDisabled={
                                        !(attempt.auto_scoring_completed && attempt.manual_scoring_completed)
                                      }
                                    >
                                      View Exam Attempt
                                    </Button>
                                  </Td>
                                  <Td>
                                    <VStack align="start" spacing={1}>
                                      {attempt.evaluated_by_username && (
                                        <Text fontSize="xs" color="gray.600">
                                          Evaluated by: <strong>{attempt.evaluated_by_username}</strong>
                                        </Text>
                                      )}
                                      {attempt.last_evaluated_on && (
                                        <Text fontSize="xs" color="gray.500">
                                          Last Evaluated:{" "}
                                          <strong>
                                            {fmtLocal(attempt.last_evaluated_on, {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                              hour12: true,
                                            })?.replace(",", " at")}
                                          </strong>
                                        </Text>
                                      )}
                                      {queueStatus === "PENDING" ? (
                                        <Button
                                          onClick={() => handleOverride(attempt.practice_attempt_id)}
                                          size="xs"
                                          variant="solid"
                                          colorScheme="purple"
                                        >
                                          Manually Score
                                        </Button>
                                      ) : (
                                        <Button
                                          onClick={() => handleOverride(attempt.practice_attempt_id)}
                                          size="xs"
                                          variant="outline"
                                          colorScheme="gray"
                                        >
                                          View / Override Score
                                        </Button>
                                      )}
                                    </VStack>
                                  </Td>
                                </Tr>

                                <Tr>
                                  <Td colSpan={6} p={0}>
                                    <Collapse
                                      in={expandedAttempts[attempt.practice_attempt_id]}
                                      animateOpacity
                                    >
                                      <Box bg="gray.50" p={3} borderTopWidth="1px">
                                        <Table variant="striped" size="sm">
                                          <Thead>
                                            <Tr>
                                              <Th>Question Set</Th>
                                              <Th>Manual Scoring</Th>
                                              <Th>Auto Scoring</Th>
                                            </Tr>
                                          </Thead>
                                          <Tbody>
                                            {attempt.question_set_score_status.map((qset) => (
                                              <Tr key={qset.practice_attempted_question_set_id}>
                                                <Td>{qset.question_set_title}</Td>
                                                <Td>
                                                  <Badge
                                                    colorScheme={
                                                      qset.manual_scoring_completed ? "green" : "red"
                                                    }
                                                  >
                                                    {qset.manual_scoring_completed
                                                      ? "Completed"
                                                      : "Pending"}
                                                  </Badge>
                                                </Td>
                                                <Td>
                                                  <Badge
                                                    colorScheme={
                                                      qset.auto_scoring_completed ? "green" : "red"
                                                    }
                                                  >
                                                    {qset.auto_scoring_completed
                                                      ? "Completed"
                                                      : "Pending"}
                                                  </Badge>
                                                </Td>
                                              </Tr>
                                            ))}
                                          </Tbody>
                                        </Table>
                                      </Box>
                                    </Collapse>
                                  </Td>
                                </Tr>
                              </React.Fragment>
                            ))}
                          </Tbody>
                        </Table>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </Box>
  );
};

export default PracticeManualAttemptStatus;